import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { Search, Plus, X, Circle, Check, XCircle } from "lucide-react";
import { api } from "../../lib/api";
import { usePageMeta } from "../../components/Layout";
import {
    STATUS_LIST, QUALITY_LIST, SOURCE_LIST, FEASIBILITY_LIST, FEASIBILITY_LABEL,
    PROJECT_TYPE_LABEL, BUDGET_LABEL, TIMING_LABEL,
} from "../../lib/labels";
import { StatusPill, SourcePill } from "../../components/admin/Pills";

const BUDGET_OPTIONS = ["under_100k", "100_150k", "150_250k", "250k_plus", "guidance"];
const PROJECT_TYPES = ["rear_extension", "side_extension", "loft", "internal_reconfiguration", "combination", "other"];

const FILTER_GROUPS = [
    { key: "status", label: "Status", options: STATUS_LIST, format: (v) => v },
    { key: "feasibility", label: "Feasibility", options: FEASIBILITY_LIST, format: (v) => FEASIBILITY_LABEL[v] },
    { key: "quality", label: "Quality", options: QUALITY_LIST, format: (v) => v },
    { key: "source", label: "Source", options: SOURCE_LIST, format: (v) => v },
    { key: "budget", label: "Budget", options: BUDGET_OPTIONS, format: (v) => BUDGET_LABEL[v] },
    { key: "project_type", label: "Project type", options: PROJECT_TYPES, format: (v) => PROJECT_TYPE_LABEL[v] },
];

function useDebounced(value, delay) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
    return v;
}

function FilterDropdown({ group, selected, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef();
    useEffect(() => {
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, []);
    const toggle = (v) => {
        const next = selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v];
        onChange(next);
    };
    return (
        <div className="relative" ref={ref}>
            <button
                onClick={() => setOpen((o) => !o)}
                className={"px-3.5 py-2 border rounded-md font-manrope font-semibold text-[12.5px] transition-colors " + (selected.length > 0 ? "border-terracotta text-terracotta bg-terracotta/5" : "border-charcoal/20 text-charcoal hover:border-charcoal/40")}
                data-testid={`filter-${group.key}`}
            >
                {group.label}{selected.length > 0 && <span className="ml-1.5">· {selected.length}</span>}
            </button>
            {open && (
                <div className="absolute top-full left-0 mt-1 z-10 bg-white border border-charcoal/15 rounded-md shadow-lg min-w-[220px] max-h-[340px] overflow-y-auto p-2">
                    {group.options.map((v) => (
                        <label key={v} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-charcoal/[0.03] cursor-pointer">
                            <input type="checkbox" checked={selected.includes(v)} onChange={() => toggle(v)} className="accent-terracotta" />
                            <span className="font-inter text-[13.5px] text-charcoal">{group.format(v)}</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}

function FeasibilityIcon({ value }) {
    if (value === "feasible") return <Check size={14} className="text-charcoal/70" />;
    if (value === "not_feasible") return <XCircle size={14} className="text-charcoal/40" />;
    return <Circle size={12} className="text-charcoal/25" />;
}

function FollowupDot({ nextAt }) {
    if (!nextAt) return null;
    try {
        const dt = parseISO(nextAt);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const tomorrow = today + 86400000;
        const t = dt.getTime();
        if (t < now.getTime() && t < today) return <span className="inline-block w-2 h-2 rounded-full bg-red-500" title="Overdue" />;
        if (t < tomorrow) return <span className="inline-block w-2 h-2 rounded-full bg-terracotta" title="Due today" />;
    } catch {}
    return null;
}

export default function EnquiriesList() {
    usePageMeta({ title: "Enquiries | Studio G4 Admin" });
    const [params, setParams] = useSearchParams();
    const [q, setQ] = useState(params.get("q") || "");
    const debouncedQ = useDebounced(q, 300);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const searchRef = useRef();

    // Focus search with /
    useEffect(() => {
        const h = (e) => {
            if (e.key === "/" && !["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) {
                e.preventDefault(); searchRef.current?.focus();
            }
        };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, []);

    // Sync q → URL
    useEffect(() => {
        const p = new URLSearchParams(params);
        if (debouncedQ) p.set("q", debouncedQ); else p.delete("q");
        p.delete("page"); // reset to page 1 on new search
        if (p.toString() !== params.toString()) setParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQ]);

    const page = parseInt(params.get("page") || "1", 10);

    const selected = useMemo(() => {
        const out = {};
        for (const g of FILTER_GROUPS) out[g.key] = params.getAll(g.key);
        return out;
    }, [params]);

    const filterCount = Object.values(selected).reduce((a, arr) => a + arr.length, 0) + (params.get("q") ? 1 : 0);

    const setFilter = (key, values) => {
        const p = new URLSearchParams(params);
        p.delete(key);
        values.forEach((v) => p.append(key, v));
        p.delete("page");
        setParams(p, { replace: true });
    };
    const clearAll = () => setParams(new URLSearchParams(), { replace: true });

    // Fetch
    useEffect(() => {
        setLoading(true);
        const query = params.toString();
        const url = `/admin/enquiries${query ? `?${query}&page_size=25` : "?page_size=25"}`;
        api.get(url).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, page: 1, page_size: 25 })).finally(() => setLoading(false));
    }, [params]);

    const items = data?.items || [];
    const total = data?.total || 0;
    const totalPages = Math.max(1, Math.ceil(total / 25));

    return (
        <div className="space-y-6" data-testid="enquiries-list-page">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2 justify-between">
                <div>
                    <h1 className="font-manrope font-bold text-charcoal text-[28px] md:text-[34px]">Enquiries</h1>
                    <p className="font-inter text-charcoal/60 text-[13px]">Showing {items.length} of {total}</p>
                </div>
                <Link to="/admin/enquiries/new" className="inline-flex items-center gap-2 font-manrope font-semibold text-[13.5px] px-4 py-2.5 rounded-md bg-terracotta text-offwhite hover:bg-terracotta-600 transition-colors" data-testid="new-enquiry-cta">
                    <Plus size={15} /> New enquiry
                </Link>
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
                <div className="relative flex-1 min-w-[220px] max-w-[420px]">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
                    <input
                        ref={searchRef}
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search name, email, postcode, description…"
                        className="w-full pl-9 pr-3 py-2.5 bg-white border border-charcoal/20 rounded-md font-inter text-[13.5px] text-charcoal focus:border-terracotta focus:outline-none"
                        data-testid="search-input"
                    />
                </div>
                {FILTER_GROUPS.map((g) => (
                    <FilterDropdown key={g.key} group={g} selected={selected[g.key]} onChange={(vs) => setFilter(g.key, vs)} />
                ))}
                {filterCount > 0 && (
                    <button onClick={clearAll} className="ml-auto text-terracotta font-manrope font-semibold text-[12.5px] hover:underline" data-testid="clear-filters">
                        Clear filters
                    </button>
                )}
            </div>

            <div className="bg-white/60 border border-charcoal/10 rounded-md overflow-x-auto">
                {loading && <div className="p-6 text-center text-charcoal/50 font-inter text-[13px]">Loading…</div>}
                {!loading && items.length === 0 && (
                    <div className="p-10 text-center">
                        <p className="font-manrope font-semibold text-charcoal">No enquiries match these filters.</p>
                        {filterCount > 0 && <button onClick={clearAll} className="mt-3 text-terracotta hover:underline font-inter text-[13.5px]">Clear filters</button>}
                    </div>
                )}
                {!loading && items.length > 0 && (
                    <table className="w-full text-[13.5px]" data-testid="enquiries-table">
                        <thead>
                            <tr className="text-left text-charcoal/50 font-manrope font-semibold text-[11px] uppercase tracking-wider">
                                <th className="px-4 py-3">Date</th>
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Postcode</th>
                                <th className="px-4 py-3">Project</th>
                                <th className="px-4 py-3">Budget</th>
                                <th className="px-4 py-3">Timing</th>
                                <th className="px-4 py-3">Source</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3 text-center">F</th>
                                <th className="px-4 py-3 text-center">Q</th>
                                <th className="px-4 py-3 text-center">FU</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((e) => {
                                const pt = e.project_types || [];
                                const firstProject = pt[0] ? PROJECT_TYPE_LABEL[pt[0]] : "—";
                                return (
                                    <tr key={e.id} onClick={() => navigate(`/admin/projects/${e.id}`)} className="cursor-pointer border-t border-charcoal/10 hover:bg-charcoal/[0.03]" data-testid={`enquiry-row-${e.id}`}>
                                        <td className="px-4 py-3 text-charcoal/70 whitespace-nowrap">{e.created_at ? format(parseISO(e.created_at), "d MMM yyyy") : ""}</td>
                                        <td className="px-4 py-3 font-manrope font-semibold text-charcoal">
                                            <span data-testid={`enquiry-row-name-${e.id}`}>
                                                {e.full_name}
                                                {Array.isArray(e.clients) && e.clients.length === 2 && (
                                                    <span
                                                        className="ml-1 inline-block text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-1 py-0.5"
                                                        data-testid={`enquiry-row-plus-one-${e.id}`}
                                                        title="Second client on record">
                                                        +1
                                                    </span>
                                                )}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-charcoal/70">{e.postcode}</td>
                                        <td className="px-4 py-3 text-charcoal/80">{firstProject}{pt.length > 1 && <span className="text-charcoal/50"> +{pt.length - 1}</span>}</td>
                                        <td className="px-4 py-3 text-charcoal/70">{BUDGET_LABEL[e.construction_budget] || "—"}</td>
                                        <td className="px-4 py-3 text-charcoal/70">{TIMING_LABEL[e.construction_timing] || "—"}</td>
                                        <td className="px-4 py-3"><SourcePill value={e.source} /></td>
                                        <td className="px-4 py-3"><StatusPill value={e.status} /></td>
                                        <td className="px-4 py-3 text-center"><FeasibilityIcon value={e.feasibility} /></td>
                                        <td className="px-4 py-3 text-center text-charcoal/70">{e.lead_quality ? e.lead_quality[0] : ""}</td>
                                        <td className="px-4 py-3 text-center"><FollowupDot nextAt={e.next_follow_up_at} /></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {totalPages > 1 && (
                <div className="flex justify-between items-center text-[13px] font-inter text-charcoal/70">
                    <div>Page {page} of {totalPages}</div>
                    <div className="flex gap-2">
                        <button
                            disabled={page <= 1}
                            onClick={() => { const p = new URLSearchParams(params); p.set("page", String(page - 1)); setParams(p); }}
                            className="px-3 py-1.5 border border-charcoal/20 rounded font-manrope font-semibold disabled:opacity-40"
                            data-testid="page-prev"
                        >← Prev</button>
                        <button
                            disabled={page >= totalPages}
                            onClick={() => { const p = new URLSearchParams(params); p.set("page", String(page + 1)); setParams(p); }}
                            className="px-3 py-1.5 border border-charcoal/20 rounded font-manrope font-semibold disabled:opacity-40"
                            data-testid="page-next"
                        >Next →</button>
                    </div>
                </div>
            )}
        </div>
    );
}
